import { descending, timeFormat } from 'd3';
import Job from '@/store/models/Job';
import Theme from '@/store/models/Theme';
import {
  PLAN_MONTHLY_ALLOWANCE,
  ANNUAL_PLANS,
  UNLIMITED_PLANS,
  UNLIMITED_DESIGNS_PLANS,
} from '@/config/account-config';
import {
  getRolloverTime,
  setBillingDetails,
  getVideosBetween,
  getNoStripeTimeLeft,
  formatTimeLeft,
} from '@/helpers/account-utils';
import { getThisMonthStart, addMonths } from '@/helpers/date-utils';

class User {
  constructor(user, subscription = []) {
    // eslint-disable-next-line no-underscore-dangle
    this.id = user._id;
    this.role = user.role;
    this.link = Object.assign(
      {},
      {
        customDomain: null,
        shortId: null,
        slug: null,
        formEnabled: false,
        analyticsEnabled: false,
      },
      user.link
    );
    this.products = user.products;
    this.email = user.email;
    this.name = user.name;
    this.stripe = user.stripe || {};
    this.emailOptOut = user.emailOptOut;
    this.dateAdded = new Date(user.dateAdded);
    this.isSubscribed = subscription.length > 0;
    this.monthlyAllowance = PLAN_MONTHLY_ALLOWANCE[user.products.video];
    this.planIsUnlimited = UNLIMITED_PLANS.includes(user.products.video);
    this.videosWatermarked = !this.isSubscribed;
    this.hasPremiumLink = user.products.link !== 'free';
    this.videoPlan = {};
    this.linkPlan = {};

    // Check for pending link cancelations
    const pendingLinkCancel = user.planChanges
      ? user.planChanges.find(x => x.actionType === 'cancel' && x.status === 'pending' && x.planType === 'link')
      : undefined;
    if (pendingLinkCancel) {
      this.linkPlan.cancelationDate = new Date(pendingLinkCancel.stopDate);
      this.linkPlan.willCancel = new Date() < this.linkPlan.cancelationDate;
    } else {
      this.linkPlan.willCancel = false;
    }

    // Check for pending video cancelations
    const pendingVideoCancel = user.planChanges
      ? user.planChanges.find(x => x.actionType === 'cancel' && x.status === 'pending' && x.planType === 'video')
      : undefined;
    if (pendingVideoCancel) {
      this.videoPlan.cancelationDate = new Date(pendingVideoCancel.stopDate);
      this.videoPlan.willCancel = new Date() < this.videoPlan.cancelationDate;
    } else if (user.canceled && Object.keys(user.canceled).length) {
      // LEGACY SUPPORT FOR USER.CANCELED
      this.videoPlan.cancelationDate = new Date(user.canceled.endDate);
      this.videoPlan.willCancel = new Date() < this.videoPlan.cancelationDate;
    } else {
      this.videoPlan.willCancel = false;
    }

    const pendingVideoPause = user.planChanges
      ? user.planChanges.find(x => x.actionType === 'pause' && (x.status === 'pending' || x.status === 'paused'))
      : undefined;

    if (pendingVideoPause) {
      this.videoPlan.resumeDate = new Date(pendingVideoPause.resumeDate);
      this.videoPlan.pauseDate = new Date(pendingVideoPause.stopDate);
      this.videoPlan.pauseRollover = pendingVideoPause.rollover;
      this.videoPlan.isPaused = new Date() < this.videoPlan.resumeDate && new Date() > this.videoPlan.pauseDate;
      this.videoPlan.willPause = new Date() < this.videoPlan.pauseDate;
    } else if (user.paused && Object.keys(user.paused).length) {
      // LEGACY SUPPORT FOR USER.PAUSED
      this.videoPlan.resumeDate = new Date(user.paused.resumeDate);
      this.videoPlan.pauseDate = new Date(user.paused.startDate);
      this.videoPlan.pauseRollover = user.paused.rollover;
      this.videoPlan.isPaused = new Date() < this.videoPlan.resumeDate && new Date() > this.videoPlan.pauseDate;
      this.videoPlan.willPause = new Date() < this.videoPlan.pauseDate;
    } else {
      // set pause props to false
      this.videoPlan.isPaused = false;
      this.videoPlan.willPause = false;
    }
    // Populate User videos via Job constructor
    this.jobs = (user.jobs || []).map(job => new Job(job)).sort((a, b) => descending(a.dateAdded, b.dateAdded));

    // Populate User designs via Theme constructor
    this.themes = (user.cards || [])
      .map(theme => new Theme(theme))
      .filter(theme => !theme.deleted)
      .sort((a, b) => descending(a.dateAdded, b.dateAdded));

    // Set usage limits
    if (UNLIMITED_DESIGNS_PLANS.includes(user.products.video)) {
      this.canCreateNewDesign = true;
    } else {
      this.canCreateNewDesign = this.themes.length < 9;
    }
    // Determine whether to show new user tips
    if (this.jobs.length === 0) {
      this.noJobs = true;
    }
    if (this.themes.length === 0) {
      this.noThemes = true;
    }

    // Set billing data if customer has a subscription
    if (subscription.length) {
      this.hasSubscription = true;
      this.stripe.id = user.stripe.id;

      // HANDLE MULTI-PRODUCT SUBSCRIPTIONS
      const videoPlan = subscription.find(x => x.plan.id.indexOf('link') === -1);
      const linkPlan = subscription.find(x => x.plan.id.indexOf('link') !== -1);

      // Add billing dates for wavve link plan
      if (linkPlan) {
        this.linkPlan.isDelinquent = linkPlan.status === 'past_due';
        this.linkPlan.planStart = new Date(linkPlan.created * 1000);
        this.linkPlan.currentPeriodStart = new Date(linkPlan.current_period_start * 1000);
        this.linkPlan.currentPeriodEnd = new Date(linkPlan.current_period_end * 1000);
        // Set Start Date Based on Billing Interval
        if (ANNUAL_PLANS.includes(linkPlan.plan.id)) {
          this.linkPlan.currentMonthStartDate = getThisMonthStart(this.linkPlan.currentPeriodStart);
        } else {
          this.linkPlan.currentMonthStartDate = this.linkPlan.currentPeriodStart;
        }
        this.linkPlan.nextBillingDate = this.linkPlan.currentPeriodEnd;
      }

      if (videoPlan) {
        this.videoPlan.isDelinquent = videoPlan.status === 'past_due';
        console.log('videoPlan', videoPlan);
        this.videoPlan.planStart = new Date(videoPlan.created * 1000);
        this.videoPlan.currentPeriodStart = new Date(videoPlan.current_period_start * 1000);
        this.videoPlan.currentPeriodEnd = new Date(videoPlan.current_period_end * 1000);
        this.monthlyAllowance = PLAN_MONTHLY_ALLOWANCE[this.products.video];
        this.rollover = getRolloverTime({
          planStartDate: this.videoPlan.planStart,
          allowance: this.monthlyAllowance,
          rolloverAllowance: this.monthlyAllowance * 2,
          jobs: this.jobs,
        });
        // Set Start Date Based on Billing Interval
        if (ANNUAL_PLANS.includes(videoPlan.plan.id)) {
          this.videoPlan.currentMonthStartDate = getThisMonthStart(this.videoPlan.currentPeriodStart);
        } else {
          this.videoPlan.currentMonthStartDate = this.videoPlan.currentPeriodStart;
        }
        this.videoPlan.nextBillingDate = this.videoPlan.currentPeriodEnd;
      }

      // Stripe stores multiple plans differently, so we must first check for this before trying to access plan data
      this.products.link = linkPlan && linkPlan.plan ? linkPlan.plan.id : 'free';
      this.products.video = videoPlan && videoPlan.plan ? videoPlan.plan.id : 'free';
      // Handle Video Specific Plan Calculations
      setBillingDetails(this);
      // Set 'free' & 'paused' users as well as 'orphaned' users (missing stripe acct but have user.plan)
    } else {
      this.hasSubscription = false;
      // always need videoPlan start date for 'free/paused' usage calculation
      this.videoPlan.currentMonthStartDate = getThisMonthStart(this.dateAdded);
      this.rollover = this.pauseRollover || 0;
      if (!this.planIsUnlimited) {
        this.timeLeft = getNoStripeTimeLeft({
          userDateAdded: this.dateAdded,
          jobs: this.jobs,
          allowance: this.monthlyAllowance + this.rollover,
        });
        this.timeLeftString = formatTimeLeft(this.timeLeft);
      } else {
        this.timeLeft = null;
        this.timeLeftString = 'Unlimited plan';
      }
    }
    this.videosThisMonth = getVideosBetween({
      jobs: this.jobs,
      startDate: this.videoPlan.currentMonthStartDate,
    });
    this.videoPlan.nextMonthStartDate = addMonths(this.videoPlan.currentMonthStartDate, 1);
    this.videoPlan.nextMonthStartDateString = timeFormat('%B %d, %Y')(this.videoPlan.nextMonthStartDate);
    this.videoPlan.currentMonthStartDateString = timeFormat('%B %d, %Y')(this.videoPlan.currentMonthStartDate);
  }
}
export default User;
